// /home/labdoo/zitech-nexus/frontend/src/pages/admin/NewsletterDashboard.tsx
import React, { useState, useEffect } from 'react';
import { newsletterAPI } from '../../services/api';
import './NewsletterDashboard.css';

// SVG Icons
const Icons = {
  Subscribers: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  Emails: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 6L12 13L2 6M22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6" />
      <path d="M12 13L22 6L2 6L12 13Z" />
      <path d="M8 11L2 17M16 11L22 17" />
    </svg>
  ),
  Open: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3" />
      <path d="M12 7V12L15 15" />
      <path d="M19 5L12 12M19 5V10M19 5H14" />
    </svg>
  ),
  Unsubscribed: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 22C16.8954 22 16 21.1046 16 20C16 18.8954 16.8954 18 18 18C19.1046 18 20 18.8954 20 20C20 21.1046 19.1046 22 18 22Z" />
      <path d="M6 22C4.89543 22 4 21.1046 4 20C4 18.8954 4.89543 18 6 18C7.10457 18 8 18.8954 8 20C8 21.1046 7.10457 22 6 22Z" />
      <path d="M8 20H16" />
      <path d="M4 8H20V20H4V8Z" />
      <path d="M16 12H8M12 8V16" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Up: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  Down: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nd-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

interface AnalyticsData {
  total_subscribers: number;
  total_unsubscribed: number;
  daily_analytics: Array<{
    date: string;
    total_subscribers: number;
    new_subscribers: number;
    unsubscribes: number;
    emails_sent: number;
    open_rate: number | string;
    click_rate: number | string;
  }>;
  last_campaign: {
    subject: string;
    sent_at: string;
    total_sent: number;
    total_opens: number;
    total_clicks: number;
    total_unsubscribes: number;
  } | null;
}

const NewsletterDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await newsletterAPI.getAnalytics();
      
      const processedData = {
        ...response.data,
        daily_analytics: response.data.daily_analytics.map((day: any) => ({
          ...day,
          open_rate: typeof day.open_rate === 'string' ? parseFloat(day.open_rate) : day.open_rate,
          click_rate: typeof day.click_rate === 'string' ? parseFloat(day.click_rate) : day.click_rate
        }))
      };
      
      setAnalytics(processedData);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculateGrowth = () => {
    if (!analytics?.daily_analytics || analytics.daily_analytics.length < 2) return 0;
    const today = analytics.daily_analytics[analytics.daily_analytics.length - 1];
    const yesterday = analytics.daily_analytics[analytics.daily_analytics.length - 2];
    if (yesterday.total_subscribers === 0) return 0;
    return ((today.total_subscribers - yesterday.total_subscribers) / yesterday.total_subscribers) * 100;
  };

  const getSafeNumber = (value: number | string | undefined, defaultValue: number = 0): number => {
    if (value === undefined || value === null) return defaultValue;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="nd-loading-container">
        <div className="nd-spinner">
          <Icons.Spinner />
        </div>
        <p className="nd-loading-text">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nd-error-container">
        <div className="nd-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p className="nd-error-text">{error}</p>
        <button onClick={fetchAnalytics} className="nd-retry-btn">Retry</button>
      </div>
    );
  }

  const growth = calculateGrowth();
  const totalEmailsSent = analytics?.daily_analytics.reduce((sum, day) => sum + (day.emails_sent || 0), 0) || 0;
  const avgOpenRate = analytics?.daily_analytics.length 
    ? (analytics.daily_analytics.reduce((sum, day) => sum + getSafeNumber(day.open_rate), 0) / analytics.daily_analytics.length)
    : 0;

  return (
    <div className="nd-container">
      {/* Header */}
      <div className="nd-header">
        <div className="nd-header-left">
          <h1 className="nd-title">Newsletter Analytics</h1>
          <p className="nd-subtitle">Monitor your newsletter performance and subscriber growth</p>
        </div>
        <div className="nd-time-range">
          <button 
            className={`nd-time-btn ${timeRange === '7d' ? 'nd-time-active' : ''}`}
            onClick={() => setTimeRange('7d')}
          >
            7 Days
          </button>
          <button 
            className={`nd-time-btn ${timeRange === '30d' ? 'nd-time-active' : ''}`}
            onClick={() => setTimeRange('30d')}
          >
            30 Days
          </button>
          <button 
            className={`nd-time-btn ${timeRange === '90d' ? 'nd-time-active' : ''}`}
            onClick={() => setTimeRange('90d')}
          >
            90 Days
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="nd-stats-grid">
        <div className="nd-stat-card">
          <div className="nd-stat-icon nd-stat-subscribers">
            <Icons.Subscribers />
          </div>
          <div className="nd-stat-content">
            <h3 className="nd-stat-label">Total Subscribers</h3>
            <div className="nd-stat-value">{analytics?.total_subscribers || 0}</div>
            <div className={`nd-stat-trend ${growth >= 0 ? 'nd-trend-up' : 'nd-trend-down'}`}>
              <span className="nd-trend-icon">
                {growth >= 0 ? <Icons.Up /> : <Icons.Down />}
              </span>
              {Math.abs(growth).toFixed(1)}% from yesterday
            </div>
          </div>
        </div>

        <div className="nd-stat-card">
          <div className="nd-stat-icon nd-stat-emails">
            <Icons.Emails />
          </div>
          <div className="nd-stat-content">
            <h3 className="nd-stat-label">Emails Sent (30d)</h3>
            <div className="nd-stat-value">{totalEmailsSent}</div>
          </div>
        </div>

        <div className="nd-stat-card">
          <div className="nd-stat-icon nd-stat-open">
            <Icons.Open />
          </div>
          <div className="nd-stat-content">
            <h3 className="nd-stat-label">Avg Open Rate</h3>
            <div className="nd-stat-value">{avgOpenRate.toFixed(1)}%</div>
          </div>
        </div>

        <div className="nd-stat-card">
          <div className="nd-stat-icon nd-stat-unsubscribed">
            <Icons.Unsubscribed />
          </div>
          <div className="nd-stat-content">
            <h3 className="nd-stat-label">Unsubscribed</h3>
            <div className="nd-stat-value">{analytics?.total_unsubscribed || 0}</div>
          </div>
        </div>
      </div>

      {/* Last Campaign */}
      {analytics?.last_campaign && (
        <div className="nd-campaign">
          <h2 className="nd-section-title">Last Campaign Performance</h2>
          <div className="nd-campaign-grid">
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Subject</span>
              <span className="nd-campaign-value">{analytics.last_campaign.subject}</span>
            </div>
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Sent</span>
              <span className="nd-campaign-value">
                <Icons.Calendar />
                {new Date(analytics.last_campaign.sent_at).toLocaleDateString()}
              </span>
            </div>
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Recipients</span>
              <span className="nd-campaign-value">{analytics.last_campaign.total_sent}</span>
            </div>
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Open Rate</span>
              <span className="nd-campaign-value nd-campaign-rate">
                {analytics.last_campaign.total_sent > 0 
                  ? ((analytics.last_campaign.total_opens / analytics.last_campaign.total_sent) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Click Rate</span>
              <span className="nd-campaign-value nd-campaign-rate">
                {analytics.last_campaign.total_sent > 0 
                  ? ((analytics.last_campaign.total_clicks / analytics.last_campaign.total_sent) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
            <div className="nd-campaign-item">
              <span className="nd-campaign-label">Unsubscribes</span>
              <span className="nd-campaign-value nd-campaign-unsub">
                {analytics.last_campaign.total_sent > 0 
                  ? ((analytics.last_campaign.total_unsubscribes / analytics.last_campaign.total_sent) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="nd-table-section">
        <h2 className="nd-section-title">Subscriber Growth</h2>
        <div className="nd-table-wrapper">
          <table className="nd-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Total Subscribers</th>
                <th>New</th>
                <th>Unsubscribes</th>
                <th>Emails Sent</th>
                <th>Open Rate</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.daily_analytics.map((day) => (
                <tr key={day.date} className="nd-table-row">
                  <td className="nd-table-date">{new Date(day.date).toLocaleDateString()}</td>
                  <td className="nd-table-total">{day.total_subscribers}</td>
                  <td className="nd-table-new">
                    <span className="nd-new-badge">
                      <Icons.Up />
                      {day.new_subscribers}
                    </span>
                  </td>
                  <td className="nd-table-unsub">
                    <span className="nd-unsub-badge">
                      <Icons.Down />
                      {day.unsubscribes}
                    </span>
                  </td>
                  <td className="nd-table-sent">{day.emails_sent}</td>
                  <td className="nd-table-rate">{getSafeNumber(day.open_rate).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NewsletterDashboard;